import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { BedDouble, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChangeRoomModal = ({ open, onClose, reservation, propertyId, onChanged }) => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState(null);

  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (open && propertyId) {
      fetchUnits();
      setSelectedUnitId(null);
    }
  }, [open, propertyId]);

  const fetchUnits = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/properties/${propertyId}/units`, { headers });
      setUnits(res.data || []);
    } catch {
      toast.error('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRoom = async () => {
    if (!selectedUnitId) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/reservations/${reservation.id}/change-room`,
        null,
        { params: { new_unit_id: selectedUnitId }, headers }
      );
      const newRoom = units.find(u => u.id === selectedUnitId);
      toast.success(`Moved to Room ${newRoom?.name || selectedUnitId}`);
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change room');
    } finally {
      setSaving(false);
    }
  };

  if (!open || !reservation) return null;

  const currentUnitId = reservation.unit_id;
  const availableUnits = units.filter(u => u.id !== currentUnitId && u.status !== 'OOO');
  const currentUnit = units.find(u => u.id === currentUnitId);

  const statusColors = {
    CLEAN: 'bg-emerald-500', DIRTY: 'bg-red-500', IN_PROGRESS: 'bg-amber-500',
    INSPECTED: 'bg-blue-500', OCCUPIED: 'bg-purple-500', OOO: 'bg-slate-400'
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="change-room-modal">
        <DialogHeader>
          <DialogTitle>Change Room</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current room */}
          <div className="p-3 bg-slate-50 rounded-lg text-sm" data-testid="change-room-current">
            <p className="text-xs font-semibold text-slate-500 mb-1">Current Room</p>
            <div className="flex items-center gap-2">
              <BedDouble size={16} className="text-slate-600" />
              <span className="font-medium">{currentUnit?.name || currentUnitId}</span>
              {currentUnit && (
                <span className={`w-2 h-2 rounded-full ${statusColors[currentUnit.status] || 'bg-slate-400'}`} />
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {reservation.guest?.name} &middot; {reservation.check_in} to {reservation.check_out}
            </p>
          </div>

          {/* Available rooms */}
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-2">Select New Room</p>
            {loading ? (
              <p className="text-center text-sm text-slate-400 py-4">Loading rooms...</p>
            ) : availableUnits.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-4">No other rooms available</p>
            ) : (
              <div className="space-y-1 max-h-[300px] overflow-y-auto" data-testid="change-room-list">
                {availableUnits.map(unit => (
                  <button
                    key={unit.id}
                    onClick={() => setSelectedUnitId(unit.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border text-sm transition ${
                      selectedUnitId === unit.id
                        ? 'border-cyan-500 bg-cyan-50 text-cyan-900'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`room-option-${unit.name}`}
                  >
                    <div className="flex items-center gap-2">
                      <BedDouble size={14} />
                      <span className="font-medium">{unit.name}</span>
                      <span className={`w-2 h-2 rounded-full ${statusColors[unit.status] || 'bg-slate-400'}`} />
                      <span className="text-xs text-slate-500">{unit.status}</span>
                    </div>
                    {selectedUnitId === unit.id && <Check size={16} className="text-cyan-600" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleChangeRoom} disabled={!selectedUnitId || saving} data-testid="change-room-confirm-btn">
              {saving ? 'Moving...' : 'Move to Selected Room'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeRoomModal;
